import React from 'react';
import { Box, Drawer, IconButton, Typography, List, ListItem, ListItemIcon, ListItemText, Divider, Tooltip, Alert, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { evaluateObjectiveRules, buildPatientSummary } from './rules';
import { AssistService } from '../../../../../service/assistService';

export default function AssistPanel({ open, onClose, general, objective }) {
  const [flags, setFlags] = React.useState([]);
  const [summary, setSummary] = React.useState('');
  const [rulesVersion, setRulesVersion] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const controllerRef = React.useRef(null);

  const runEvaluation = React.useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setError('');
    setLoading(true);
    AssistService.evaluate({ general, objective, options: { includeSummary: true }, signal: controller.signal })
      .then(({ data }) => {
        const result = data || {};
        setFlags(result.flags || []);
        setSummary(result.summary || '');
        setRulesVersion(result.rulesVersion || '');
      })
      .catch(() => {
        // fallback to local rules
        setFlags(evaluateObjectiveRules(objective, general));
        setSummary(buildPatientSummary(general, objective));
        setRulesVersion('fallback-local');
        setError('Assist service unavailable; using local checks.');
      })
      .finally(() => setLoading(false));
  }, [general, objective]);

  // Evaluate once when panel opens
  React.useEffect(() => {
    if (open) {
      runEvaluation();
    } else if (controllerRef.current) {
      controllerRef.current.abort();
    }
    return () => { if (controllerRef.current) controllerRef.current.abort(); };
  }, [open, runEvaluation]);

  const iconFor = (sev) => {
    switch (sev) {
      case 'warning': return <WarningIcon color="warning" />;
      case 'error': return <ErrorIcon color="error" />;
      default: return <InfoIcon color="info" />;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      hideBackdrop
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      ModalProps={{ disableEnforceFocus: true }}
      PaperProps={{ sx: { width: 420, p: 2 } }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Assist</Typography>
        <Box>
          <Tooltip title="Re-evaluate">
            <span>
              <IconButton onClick={runEvaluation} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        Suggestions and checks based on General + Objective tabs.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Flags</Typography>
      {loading && (
        <Alert severity="info" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} /> Evaluating…
        </Alert>
      )}
  {error && <Alert severity="info" sx={{ mb: 1 }}>{error}</Alert>}
      {flags.length === 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>No issues detected with current inputs.</Alert>
      ) : (
        <List dense sx={{ mb: 2 }}>
          {flags.map(f => (
            <ListItem key={f.id} alignItems="flex-start">
              <ListItemIcon>{iconFor(f.severity)}</ListItemIcon>
              <ListItemText primary={f.message} secondary={f.severity.toUpperCase()} />
            </ListItem>
          ))}
        </List>
      )}

      <Divider sx={{ mb: 2 }} />
  <Typography variant="subtitle1" sx={{ mb: 1 }}>Patient Summary (draft)</Typography>
      {summary ? (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{summary}</Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">Fill in fields on General/Objective to see a draft summary.</Typography>
      )}

      {rulesVersion && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary">Rules: {rulesVersion}</Typography>
        </Box>
      )}
    </Drawer>
  );
}
